// バリデーションユーティリティ

import { FormData, ValidationErrors } from '../types';

/**
 * 数値文字列の検証
 */
export function validateNumericString(
  value: string,
  fieldName: string,
  options: {
    min?: number;
    max?: number;
    allowZero?: boolean;
    integer?: boolean;
  } = {}
): string | null {
  if (!value.trim()) {
    return `${fieldName}を入力してください。`;
  }

  const num = Number(value);
  
  if (isNaN(num)) {
    return `${fieldName}は数値で入力してください。`;
  }

  if (options.integer && !Number.isInteger(num)) {
    return `${fieldName}は整数で入力してください。`;
  }

  if (options.min !== undefined) {
    if (options.allowZero) {
      if (num < options.min) {
        return `${fieldName}は${options.min}以上で入力してください。`;
      }
    } else {
      if (num <= options.min) {
        return `${fieldName}は${options.min}より大きい値で入力してください。`;
      }
    }
  }

  if (options.max !== undefined && num > options.max) {
    return `${fieldName}は${options.max}以下で入力してください。`;
  }

  return null;
}

/**
 * フォーム全体の検証
 */
export function validateForm(formData: FormData): ValidationErrors {
  const errors: ValidationErrors = {};

  // 部材名の検証
  if (!formData.materialName) {
    errors.materialName = '部材名を選択してください。';
  }

  // 板材1の検証
  const veneerWidth1Error = validateNumericString(
    formData.veneerWidth1,
    '板材1の幅',
    { min: 0, integer: true }
  );
  if (veneerWidth1Error) errors.veneerWidth1 = veneerWidth1Error;

  const veneerHeight1Error = validateNumericString(
    formData.veneerHeight1,
    '板材1の高さ',
    { min: 0, integer: true }
  );
  if (veneerHeight1Error) errors.veneerHeight1 = veneerHeight1Error;

  // 板材2の検証
  const veneerWidth2Error = validateNumericString(
    formData.veneerWidth2,
    '板材2の幅',
    { min: 0, integer: true }
  );
  if (veneerWidth2Error) errors.veneerWidth2 = veneerWidth2Error;

  const veneerHeight2Error = validateNumericString(
    formData.veneerHeight2,
    '板材2の高さ',
    { min: 0, integer: true }
  );
  if (veneerHeight2Error) errors.veneerHeight2 = veneerHeight2Error;

  // 切り出す板の検証
  const pieceWidthError = validateNumericString(
    formData.pieceWidth,
    '切り出す板の幅',
    { min: 0, integer: true }
  );
  if (pieceWidthError) errors.pieceWidth = pieceWidthError;

  const pieceHeightError = validateNumericString(
    formData.pieceHeight,
    '切り出す板の高さ',
    { min: 0, integer: true }
  );
  if (pieceHeightError) errors.pieceHeight = pieceHeightError;

  // 刃幅の検証
  const kerfError = validateNumericString(
    formData.kerf,
    '刃幅',
    { min: 0, allowZero: true, integer: true }
  );
  if (kerfError) errors.kerf = kerfError;

  return errors;
}

/**
 * 論理的な検証（数値検証後に実行）
 */
export function validateLogic(formData: FormData): ValidationErrors {
  const errors: ValidationErrors = {};

  // 基本的な数値検証をパスしているかチェック
  const basicErrors = validateForm(formData);
  if (Object.keys(basicErrors).length > 0) {
    return basicErrors;
  }

  const veneerWidth1 = Number(formData.veneerWidth1);
  const veneerHeight1 = Number(formData.veneerHeight1);
  const veneerWidth2 = Number(formData.veneerWidth2);
  const veneerHeight2 = Number(formData.veneerHeight2);
  const pieceWidth = Number(formData.pieceWidth);
  const pieceHeight = Number(formData.pieceHeight);

  // 切り出す板が板材に収まるかチェック
  const canFitVeneer1 = 
    (pieceWidth <= veneerWidth1 && pieceHeight <= veneerHeight1) ||
    (formData.allowRotation1 && (pieceHeight <= veneerWidth1 && pieceWidth <= veneerHeight1));

  const canFitVeneer2 = 
    (pieceWidth <= veneerWidth2 && pieceHeight <= veneerHeight2) ||
    (formData.allowRotation2 && (pieceHeight <= veneerWidth2 && pieceWidth <= veneerHeight2));

  if (!canFitVeneer1 && !canFitVeneer2) {
    errors.pieceWidth = '切り出す板のサイズが大きすぎます。';
    errors.pieceHeight = 'どちらの板材にも収まりません。';
  }

  return errors;
}

/**
 * リアルタイム検証（入力中の検証）
 */
export function validateField(
  fieldName: keyof FormData,
  value: string | boolean,
  _formData: FormData
): string | null {
  if (typeof value === 'boolean') {
    return null; // ブール値フィールドはエラーなし
  }

  switch (fieldName) {
    case 'materialName':
      return value ? null : '部材名を選択してください。';

    case 'veneerWidth1':
      return validateNumericString(value, '板材1の幅', { min: 0, integer: true });

    case 'veneerHeight1':
      return validateNumericString(value, '板材1の高さ', { min: 0, integer: true });

    case 'veneerWidth2':
      return validateNumericString(value, '板材2の幅', { min: 0, integer: true });

    case 'veneerHeight2':
      return validateNumericString(value, '板材2の高さ', { min: 0, integer: true });

    case 'pieceWidth':
      return validateNumericString(value, '切り出す板の幅', { min: 0, integer: true });

    case 'pieceHeight':
      return validateNumericString(value, '切り出す板の高さ', { min: 0, integer: true });

    case 'kerf':
      return validateNumericString(value, '刃幅', { min: 0, allowZero: true, integer: true });

    default:
      return null;
  }
}

/**
 * エラーメッセージの表示判定
 */
export function shouldShowError(
  fieldName: keyof FormData,
  error: string | undefined,
  touched: { [key: string]: boolean }
): boolean {
  return !!(error && touched[fieldName]);
}